import { Box, Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import {
  CubeMeasureEnum,
  FeatureEnum,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  isBlueberryFeature,
  isBlueberryFlowerFeature,
} from '../../../services/data/impl/cube/cubeUtils';
import { defaultUserSettings } from '../../../services/entities/hooks/settings/useSettings';
import { UnitsEnum } from '../../../services/entities/impl/blfApi/apiConstants';
import { useFeatureFlags } from '../../../services/featureFlags/hooks/useFeatureFlags';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { getUnitSymbol, MeasurementTypes } from '../../commonUtils';
import { ImageInfoProps } from '../../imageViewer/ImageInfoSlot';
import { ColorSlot } from '../../imageViewer/slots/ColorSlot';
import { NumericSlot } from '../../imageViewer/slots/NumericSlot';
import { selectCurrFeature, selectCurrMeasure } from '../../select/selectSlice';
import { useUnits } from '../../settings/hooks/useUnits';
import { ScanData } from '../scanDataDefinitions';
import { useBlockAverage } from './useBlockAverage';
import useFilterScanData, { ScanDataFilterEnum } from './useFilterScanData';
type TraveledDistanceIndicatorProps = {
  hasTraveledDistance: boolean;
};

const TraveledDistanceIndicator = ({
  hasTraveledDistance,
}: TraveledDistanceIndicatorProps) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: '0',
        right: '0',
        width: '0.5rem',
        height: '0.5rem',
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: hasTraveledDistance ? 'green' : 'red',
        borderRadius: '50%',
      }}
    />
  );
};

type FeatureSlotProps = {
  currPointData: ScanData;
  t: ReturnType<typeof useTranslation>['t'];
  units: UnitsEnum;
  mean: number | null;
  currMeasure: CubeMeasureEnum;
  hasTraveledDistance: boolean;
  showTooltip?: boolean;
};

const useCommonSlots = ({
  currPointData,
  feature,
  t,
  units,
  mean,
  hasTraveledDistance,
  showTooltip = false,
}: FeatureSlotProps & { feature: FeatureEnum }) => {
  const slot0: ImageInfoProps = {
    label: t(feature, { ns: 'common' }),
    children: <NumericSlot value={currPointData?.raw_count} />,
  };

  const slot1: ImageInfoProps = {
    label: `${t(feature, { ns: 'common' })} / ${getUnitSymbol(
      units,
      MeasurementTypes.meterOrFeet,
      t
    )}`,
    children: (
      <NumericSlot value={currPointData && currPointData.count_per_meter} />
    ),
  };

  const slot2: ImageInfoProps = {
    label: `${t('blockAvg', { ns: 'ranch-view' })} / ${getUnitSymbol(
      units,
      MeasurementTypes.meterOrFeet,
      t
    )}`,
    children: showTooltip ? (
      <Tooltip
        title={
          <>
            <Typography>
              {t(
                'Indicates whether distance traveled was used in calculation.',
                { ns: 'ranch-view' }
              )}
            </Typography>
            <Typography
              sx={{
                background: '#ddd',
                borderRadius: '4px',
                fontFamily: 'monospace',
                color: 'black',
                px: 1,
              }}
            >
              <span style={{ color: 'green' }}>Green</span> = (Total Sum
              Estimated Count) / (Total Sum Distance Traveled)
            </Typography>
            <Typography
              sx={{
                background: '#ddd',
                borderRadius: '4px',
                fontFamily: 'monospace',
                color: 'black',
                px: 1,
                mt: 1,
              }}
            >
              <span style={{ color: 'red' }}>Red</span> =
              Average(count_per_meter)
            </Typography>
          </>
        }
      >
        <Box sx={{ position: 'relative', paddingRight: '0.5rem' }}>
          <NumericSlot value={mean} />
          <TraveledDistanceIndicator
            hasTraveledDistance={hasTraveledDistance}
          />
        </Box>
      </Tooltip>
    ) : (
      <NumericSlot value={mean} />
    ),
  };

  return [slot0, slot1, slot2];
};

const featureSlotsMap: {
  [key: string]: (props: FeatureSlotProps) => ImageInfoProps[];
} = {
  [FeatureEnum.none]: ({ t }) => {
    const slot0: ImageInfoProps = {
      label: t('No Data Found', { ns: 'common' }),
      children: null,
    };
    return [slot0];
  },
  [FeatureEnum.berries]: (props) => {
    if (props.currMeasure === CubeMeasureEnum.detectionFactAvgCountPerMeter) {
      return useCommonSlots({ ...props, feature: FeatureEnum.berries });
    }
    const slot0: ImageInfoProps = {
      label: props.t(CubeMeasureEnum.DetectionFactHasColorHSV, {
        ns: 'common',
      }),
      children: <ColorSlot value={props.currPointData?.color_hsv} />,
    };

    const slot1: ImageInfoProps = {
      label: `${props.t('Avg Berry Diameter', {
        ns: 'common',
      })} (${getUnitSymbol(
        props.units,
        MeasurementTypes.millimeterOrInch,
        props.t
      )})`,
      children: (
        <NumericSlot
          value={props.currPointData && props.currPointData?.detection_length}
        />
      ),
    };

    const slot2: ImageInfoProps = {
      label: `${props.t('# Berries', { ns: 'ranch-view' })}`,
      children: <NumericSlot value={props.currPointData?.raw_count} />,
    };

    return [slot0, slot1, slot2];
  },
  [FeatureEnum.bud]: (props) =>
    useCommonSlots({ ...props, feature: FeatureEnum.bud }),
  [FeatureEnum.clusters]: (props) =>
    useCommonSlots({ ...props, feature: FeatureEnum.clusters }),
  [FeatureEnum.growth]: (props) =>
    useCommonSlots({ ...props, feature: FeatureEnum.growth }),
  [FeatureEnum.inflorescence]: (props) =>
    useCommonSlots({ ...props, feature: FeatureEnum.inflorescence }),
  [FeatureEnum.leaf]: (props) =>
    useCommonSlots({ ...props, feature: FeatureEnum.leaf }),
  [FeatureEnum.noGrowth]: (props) =>
    useCommonSlots({ ...props, feature: FeatureEnum.noGrowth }),
  [FeatureEnum.nodes]: (props) =>
    useCommonSlots({ ...props, feature: FeatureEnum.nodes }),
  [FeatureEnum.shoot]: (props) => {
    if (props.currMeasure === CubeMeasureEnum.detectionFactAvgCountPerMeter) {
      return useCommonSlots({ ...props, feature: FeatureEnum.shoot });
    }

    const slot0: ImageInfoProps = {
      label: props.t(FeatureEnum.shoot, { ns: 'common' }),
      children: <NumericSlot value={props.currPointData?.raw_count} />,
    };

    const slot1: ImageInfoProps = {
      label: `${props.t('Average Length', {
        ns: 'ranch-view',
      })} (${getUnitSymbol(
        props.units,
        MeasurementTypes.millimeterOrInch,
        props.t
      )})`,
      children: (
        <NumericSlot
          value={props.currPointData && props.currPointData.detection_length}
        />
      ),
    };

    const slot2: ImageInfoProps = {
      label: `${props.t('block', { ns: 'common' })} ${props.t('Avg. Length', {
        ns: 'ranch-view',
      })} (${getUnitSymbol(
        props.units,
        MeasurementTypes.millimeterOrInch,
        props.t
      )})`,
      children: <NumericSlot value={props.mean} />,
    };

    return [slot0, slot1, slot2];
  },
  [FeatureEnum.earlyCluster]: (props) =>
    useCommonSlots({ ...props, feature: FeatureEnum.earlyCluster }),
  [FeatureEnum.earlyShoot]: (props) => {
    if (props.currMeasure === CubeMeasureEnum.detectionFactAvgCountPerMeter) {
      return useCommonSlots({ ...props, feature: FeatureEnum.earlyShoot });
    }

    const slot0: ImageInfoProps = {
      label: props.t(FeatureEnum.earlyShoot, { ns: 'common' }),
      children: <NumericSlot value={props.currPointData?.raw_count} />,
    };

    const slot1: ImageInfoProps = {
      label: `${props.t('Average Length', {
        ns: 'ranch-view',
      })} (${getUnitSymbol(
        props.units,
        MeasurementTypes.millimeterOrInch,
        props.t
      )})`,
      children: (
        <NumericSlot
          value={props.currPointData && props.currPointData.detection_length}
        />
      ),
    };

    const slot2: ImageInfoProps = {
      label: `${props.t('block', { ns: 'common' })} ${props.t('Avg. Length', {
        ns: 'ranch-view',
      })} (${getUnitSymbol(
        props.units,
        MeasurementTypes.millimeterOrInch,
        props.t
      )})`,
      children: <NumericSlot value={props.mean} />,
    };

    return [slot0, slot1, slot2];
  },
  [FeatureEnum.trunks]: (props) =>
    useCommonSlots({ ...props, feature: FeatureEnum.trunks }),
};

const useScanDataInfoSlots = (mean: ReturnType<typeof useBlockAverage>) => {
  const currMeasure = useAppSelector(selectCurrMeasure);
  const currFeature = useAppSelector(selectCurrFeature);
  const { t } = useTranslation();
  const units = useUnits();
  const { Diagnostics } = useFeatureFlags();
  // Filter the scan data for the current point and selected service
  const {
    data: currPointData,
    isLoading: isPointLoading,
    error: isPointError,
  } = useFilterScanData({
    activeFilters: [ScanDataFilterEnum.point_id],
  });

  const infoSlots = useMemo(() => {
    if (
      !currPointData ||
      isBlueberryFeature(currFeature) ||
      isBlueberryFlowerFeature(currFeature)
    )
      return null;

    if (!(currFeature in featureSlotsMap))
      throw Error(`Feature ${currFeature} must be mapped for Image Viewer`);

    return featureSlotsMap[currFeature]({
      currPointData: currPointData[0],
      t,
      units: units || defaultUserSettings.units,
      mean: mean.data,
      currMeasure,
      hasTraveledDistance: mean.hasTraveledDistance,
      showTooltip: Diagnostics,
    });
  }, [
    currPointData,
    currFeature,
    t,
    units,
    mean.data,
    mean.hasTraveledDistance,
    currMeasure,
    Diagnostics,
  ]);

  return {
    data: infoSlots,
    isLoading: isPointLoading,
    error: isPointError,
  };
};

export default useScanDataInfoSlots;
